// FAQ (sections/faq.liquid): builds the category tabs from the Collapsible rows blocks (their "Tab label"), adds the
// Most popular tab, keeps one question open at a time, and writes the FAQ structured data from the questions.
(() => {
  if (window.themeFaq) return;

  const text = (element) => (element?.textContent || '').replace(/\s+/g, ' ').trim();

  const init = (faq) => {
    const stream = faq.querySelector('.faq__stream');
    if (!stream) return;
    stream.querySelector(':scope > .faq__tabs')?.remove();
    const categories = [...stream.querySelectorAll(':scope > .collapsible-rows')];
    const rows = [...stream.querySelectorAll('.collapsible-row')];

    // One question open at a time: native exclusive <details>.
    rows.forEach((row) => {
      if (faq.dataset.multiple === 'true') row.removeAttribute('name');
      else row.setAttribute('name', `faq-${faq.dataset.sectionId}`);
    });

    // Tabs
    const popularLabel = faq.dataset.popular;
    const hasPopular = popularLabel && rows.some((row) => row.dataset.popular === 'true');
    const tabs = [];
    if (hasPopular) tabs.push({ label: popularLabel, key: 'popular' });
    categories.forEach((category, index) => {
      tabs.push({ label: text(category.querySelector('.collapsible-rows__tab-label')) || `${index + 1}`, key: index, category });
    });

    const select = (key) => {
      faq.dataset.filter = key === 'popular' ? 'popular' : 'category';
      categories.forEach((category, index) => {
        const hide = key !== 'popular' && index !== key;
        category.toggleAttribute('data-faq-hidden', hide);
      });
      nav.querySelectorAll('.faq__tab').forEach((button) => button.setAttribute('aria-selected', String(button.dataset.key === String(key))));
    };

    const nav = document.createElement('div');
    nav.className = 'faq__tabs';
    nav.setAttribute('role', 'tablist');
    tabs.forEach((tab) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'faq__tab';
      button.setAttribute('role', 'tab');
      button.dataset.key = String(tab.key);
      button.textContent = tab.label;
      button.addEventListener('click', () => select(tab.key));
      nav.append(button);
    });

    if (categories.length && tabs.length > 1) {
      stream.insertBefore(nav, categories[0]);
      const head = [...stream.children].slice(0, [...stream.children].indexOf(nav));
      head.forEach((item) => item.setAttribute('data-faq-head', ''));
      faq.style.setProperty('--faq-head-rows', String(Math.max(head.length, 1)));
      select(tabs[0].key);
    } else {
      categories.forEach((category) => category.removeAttribute('data-faq-hidden'));
      delete faq.dataset.filter;
    }
    faq.faqSelect = (category) => {
      const index = categories.indexOf(category);
      if (index !== -1 && faq.dataset.filter) select(index);
    };

    // Structured data (search engines read JSON-LD added by scripts).
    faq.querySelector(':scope > script[data-faq-schema]')?.remove();
    if (faq.dataset.structuredData === 'true' && rows.length) {
      const schema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: rows.map((row) => ({
          '@type': 'Question',
          name: text(row.querySelector('.collapsible-row__heading')),
          acceptedAnswer: { '@type': 'Answer', text: row.querySelector('.collapsible-row__text')?.innerHTML.trim() || '' },
        })).filter((item) => item.name),
      };
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset.faqSchema = '';
      script.textContent = JSON.stringify(schema);
      faq.append(script);
    }
  };

  // Questions in two columns (desktop): each column stacks on its own, so opening a question only pushes the questions
  // below it in its column. Odd questions go left, even right; every item spans as many 2px grid rows as it is tall.
  const UNIT = 2;
  const desktop = window.matchMedia('(min-width: 750px)');
  const pack = (faq) => {
    const stream = faq.querySelector('.faq__stream');
    if (!stream) return;
    const on = faq.dataset.layout === 'two_columns' && desktop.matches;
    faq.toggleAttribute('data-masonry', on);
    const items = [...stream.querySelectorAll(':scope > :not(.collapsible-rows), :scope > .collapsible-rows .collapsible-row')];
    items.forEach((item) => { item.style.gridRowEnd = ''; item.style.gridColumn = ''; item.style.gridRowStart = ''; });
    if (!on) return;
    const beside = faq.dataset.tabsDesktop === 'true' && faq.dataset.tabsPosition === 'beside';
    let column = 0;
    const span = (item) => {
      const style = getComputedStyle(item);
      return Math.max(1, Math.ceil((item.getBoundingClientRect().height + parseFloat(style.marginTop) + parseFloat(style.marginBottom)) / UNIT));
    };
    // Tabs beside the heading reserve the heading's height, so both question columns start on the same line.
    const headSpan = items.filter((item) => item.hasAttribute('data-faq-head') && item.offsetParent).reduce((sum, item) => sum + span(item), 0);
    items.forEach((item) => {
      if (!item.offsetParent) return;
      const own = span(item);
      item.style.gridRowEnd = `span ${beside && item.classList.contains('faq__tabs') ? Math.max(own, headSpan) : own}`;
      if (item.classList.contains('collapsible-row')) {
        item.style.gridColumn = String((column % 2) + 1);
        column += 1;
      } else if (beside && item.classList.contains('faq__tabs')) {
        item.style.gridColumn = '2';
        item.style.gridRowStart = '1';
      }
    });
  };

  const observers = new WeakMap();
  const watch = (faq) => {
    observers.get(faq)?.disconnect();
    if (!('ResizeObserver' in window)) return pack(faq);
    let frame;
    const observer = new ResizeObserver(() => { cancelAnimationFrame(frame); frame = requestAnimationFrame(() => pack(faq)); });
    faq.querySelectorAll('.faq__stream, .faq__stream .collapsible-row, .faq__stream > :not(.collapsible-rows)').forEach((item) => observer.observe(item));
    observers.set(faq, observer);
    pack(faq);
  };

  const setup = (faq) => { init(faq); watch(faq); };
  const initAll = (root = document) => root.querySelectorAll('.faq').forEach(setup);
  desktop.addEventListener('change', () => document.querySelectorAll('.faq').forEach(pack));
  document.addEventListener('click', (event) => {
    const faq = event.target.closest?.('.faq__tab') && event.target.closest('.faq');
    if (faq) requestAnimationFrame(() => pack(faq));
  });
  window.themeFaq = { init, initAll };
  initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  // Theme editor: a re-rendered section leaves; stop watching its old rows so they can be freed.
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelectorAll?.('.faq').forEach((faq) => { observers.get(faq)?.disconnect(); observers.delete(faq); }));
  // Theme editor: selecting a question or category shows its tab.
  document.addEventListener('shopify:block:select', (event) => {
    const category = event.target.closest?.('.collapsible-rows');
    const faq = event.target.closest?.('.faq');
    if (faq && category) faq.faqSelect?.(category);
    if (faq) requestAnimationFrame(() => pack(faq));
    if (event.target.matches?.('.faq .collapsible-row')) event.target.open = true;
  });
})();
